import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Heart, Smile, Download, Camera, Clock } from "lucide-react";
import AlbumButton from "@/components/PhotoAlbumButton";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const day = (iso) => new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "long" });

function Reactions({ ph, onReact }) {
  return (
    <div className="flex gap-2">
      {[["coeur", Heart, "text-rose-500", "bg-rose-50 border-rose-200"], ["sourire", Smile, "text-amber-500", "bg-amber-50 border-amber-200"]].map(([r, Icon, color, on]) => (
        <button key={r} onClick={() => onReact(ph, ph.my_reaction === r ? "" : r)} aria-pressed={ph.my_reaction === r}
          data-testid={`family-photo-react-${r}-${ph.id}`}
          className={`w-11 h-11 rounded-full border flex items-center justify-center transition-colors ${ph.my_reaction === r ? on : "bg-white border-gray-200 hover:bg-gray-50"}`}>
          <Icon className={`w-5 h-5 ${color}`} fill={ph.my_reaction === r ? "currentColor" : "none"} />
        </button>
      ))}
    </div>
  );
}

const SOON = 14 * 24 * 3600 * 1000;
const expiringSoon = (photos) => photos.filter((p) => Date.parse(p.expires_at) - Date.now() < SOON);

export function useFamilyPhotos() {
  const [photos, setPhotos] = useState([]);
  useEffect(() => { api.get("/family/photos").then((r) => setPhotos(r.data)).catch(() => {}); }, []);
  return [photos, setPhotos];
}

// Pastille en haut de l'espace : nouvelles photos et rappel avant suppression.
export function FamilyPhotosBadge({ photos }) {
  const fresh = photos.filter((p) => p.new).length;
  const soon = expiringSoon(photos).length;
  if (!fresh && !soon) return null;
  const go = () => document.getElementById("family-photos")?.scrollIntoView({ behavior: "smooth", block: "start" });
  return (
    <div className="flex flex-wrap justify-center gap-3 mb-6">
      {fresh > 0 && (
        <button onClick={go} data-testid="family-new-photos-badge"
          className="inline-flex items-center gap-2 rounded-full bg-amber-400 text-amber-950 font-bold px-5 h-11 shadow-sm hover:bg-amber-300 transition-colors">
          <span className="w-2 h-2 rounded-full bg-amber-900 soft-pulse" /><Camera className="w-4 h-4" /> {fresh} nouvelle{fresh > 1 ? "s" : ""} photo{fresh > 1 ? "s" : ""} de l'atelier
        </button>
      )}
      {soon > 0 && (
        <button onClick={go} data-testid="family-photos-expiry-badge"
          className="inline-flex items-center gap-2 rounded-full bg-white border border-amber-300 text-amber-900 font-semibold px-5 h-11 hover:bg-amber-50 transition-colors">
          <Clock className="w-4 h-4" /> {soon} photo{soon > 1 ? "s" : ""} bientôt supprimée{soon > 1 ? "s" : ""}
        </button>
      )}
    </div>
  );
}

// Photos des ateliers partagées par l'équipe (gardées 3 mois).
export default function FamilyPhotos({ patientName, photos, setPhotos }) {
  const [open, setOpen] = useState(null);
  const ref = useRef(null);
  const hasNew = photos.some((p) => p.new);
  useEffect(() => {
    if (!hasNew || !ref.current) return undefined;
    const obs = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) return;
      obs.disconnect();
      api.post("/family/photos/seen").then(() => setTimeout(() => setPhotos((l) => l.map((p) => ({ ...p, new: false }))), 4000)).catch(() => {});
    }, { threshold: 1 });
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [hasNew, setPhotos]);
  const soon = expiringSoon(photos);
  const react = async (ph, reaction) => {
    try {
      await api.post(`/family/photos/${ph.id}/reaction`, { reaction });
      const upd = (p) => (p.id === ph.id ? { ...p, my_reaction: reaction } : p);
      setPhotos((l) => l.map(upd)); setOpen((o) => (o ? upd(o) : o));
    } catch { toast.error("Enregistrement impossible."); }
  };
  if (!photos.length) return null;
  return (
    <section id="family-photos" className="rounded-3xl bg-white border border-border/60 p-6 mb-8 scroll-mt-32" data-testid="family-photos-section">
      <h3 ref={ref} className="font-serif-display text-2xl mb-1 flex items-center gap-2"><Camera className="w-6 h-6 text-amber-600" /> Les photos des ateliers</h3>
      <p className="text-gray-500 mb-4">Des moments partagés par l'équipe avec {patientName}. Pensez à télécharger celles que vous aimez : elles restent ici 3 mois.</p>
      {soon.length > 0 && (
        <div className="rounded-2xl bg-amber-50 border border-amber-200 p-4 mb-4 text-amber-900" data-testid="family-photos-expiry-reminder">
          {soon.length} photo{soon.length > 1 ? "s seront supprimées" : " sera supprimée"} avant le {day(soon.map((p) => p.expires_at).sort().pop())} : pensez à créer l'album pour les garder.
        </div>
      )}
      <AlbumButton url="/family/photos/album/pdf" name={patientName} testId="family-photo-album" className="mb-5" />
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        {photos.map((ph) => (
          <button key={ph.id} onClick={() => setOpen(ph)} className="text-left rounded-2xl overflow-hidden border border-gray-100 hover:shadow-md transition-shadow" data-testid={`family-photo-${ph.id}`}>
            <span className="relative block">
              <img src={`${BACKEND}${ph.image_url}`} alt={ph.caption} className="w-full h-40 object-cover" loading="lazy" />
              {ph.new && <span className="absolute top-2 left-2 rounded-full bg-amber-400 text-amber-950 text-xs font-black px-2.5 py-1" data-testid={`family-photo-new-${ph.id}`}>Nouveau</span>}
            </span>
            <div className="p-2">
              <p className="text-sm font-medium text-gray-800 truncate">{ph.caption || "Atelier"}</p>
              <p className="text-xs text-gray-500">{day(ph.created_at)}</p>
            </div>
          </button>
        ))}
      </div>
      <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
        <DialogContent className="max-w-3xl" data-testid="family-photo-dialog">
          {open && (
            <>
              <DialogTitle className="font-serif-display text-2xl">{open.caption || "Atelier"}</DialogTitle>
              <img src={`${BACKEND}${open.image_url}`} alt={open.caption} className="w-full max-h-[65vh] object-contain rounded-xl bg-gray-50" />
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm text-gray-500">Partagée par {open.by_name || "l'équipe"} le {day(open.created_at)} · visible jusqu'au {day(open.expires_at)}</p>
                <div className="flex items-center gap-3">
                  <Reactions ph={open} onReact={react} />
                  <a href={`${BACKEND}${open.image_url}`} download={`photo-atelier-${open.id}.${open.image_url.split(".").pop()}`} data-testid="family-photo-download"
                    className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-emerald-700 text-white font-semibold hover:bg-emerald-800">
                    <Download className="w-4 h-4" /> Télécharger
                  </a>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
